/**
 * Evidence Proxy Composable
 *
 * Handles fetching Evidence.dev report files from Firebase Storage
 * with caching, authentication, and error handling.
 */

import { ref, type Ref } from 'vue'
import { getDownloadURL, ref as storageRef, getMetadata } from 'firebase/storage'
import { storage, auth } from '@/firebase'
import { getEvidenceCache, resetEvidenceCacheForLogout, type CacheStats } from './useEvidenceCache'
import createLogger from '@/utils/logger'

// Re-export CacheStats for convenience
export type { CacheStats }

const logger = createLogger('composables/useEvidenceProxy')

// Storage path prefix for Evidence build files
const STORAGE_PREFIX = 'evidence-build'

// Retry configuration
const MAX_RETRIES = 3
const INITIAL_RETRY_DELAY = 1000 // 1 second

// Content type mappings
const CONTENT_TYPE_MAP: Record<string, string> = {
  html: 'text/html',
  js: 'application/javascript',
  mjs: 'application/javascript',
  css: 'text/css',
  json: 'application/json',
  parquet: 'application/octet-stream',
  arrow: 'application/octet-stream',
  wasm: 'application/wasm',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  ico: 'image/x-icon',
  woff: 'font/woff',
  woff2: 'font/woff2',
  ttf: 'font/ttf',
  webmanifest: 'application/manifest+json',
}

/**
 * Get content type from file path
 */
function getContentType(path: string): string {
  const ext = path.split('.').pop()?.toLowerCase() || ''
  return CONTENT_TYPE_MAP[ext] || 'application/octet-stream'
}

/**
 * Sleep for specified milliseconds
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Track in-flight requests for deduplication
 */
const inFlightRequests = new Map<string, Promise<Blob>>()

/**
 * Full `data/*.parquet` bodies for this page session.
 * Key is a hash of `data/manifest.json` plus the object path.
 * Range slices are never stored here.
 */
const parquetSessionCache = new Map<string, Blob>()
const parquetSessionInflight = new Map<string, Promise<Blob>>()
let manifestHashPromise: Promise<string> | null = null
let parquetSessionEpoch = 0

function clearParquetSessionCache(): void {
  parquetSessionEpoch += 1
  parquetSessionCache.clear()
  parquetSessionInflight.clear()
  manifestHashPromise = null
}

function parquetSessionKey(path: string, manifestHash: string): string {
  return `${manifestHash}:${path}`
}

function hexDigest(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let hex = ''
  for (const byte of bytes) {
    hex += byte.toString(16).padStart(2, '0')
  }
  return hex
}

/**
 * One `bytes=` range. `end` is inclusive, matching Content-Range.
 * Multiple ranges return null so the caller does not treat a slice as the object.
 */
function parseSingleByteRange(
  rangeHeader: string,
  total: number,
): { start: number; end: number } | null {
  const header = rangeHeader.trim()
  if (header.includes(',')) return null
  const match = /^bytes=(\d*)-(\d*)$/i.exec(header)
  if (!match || total <= 0) return null
  const startStr = match[1] ?? ''
  const endStr = match[2] ?? ''
  if (startStr === '' && endStr === '') return null

  if (startStr === '') {
    const suffix = Number(endStr)
    if (!Number.isFinite(suffix) || suffix <= 0) return null
    const length = Math.min(suffix, total)
    return { start: total - length, end: total - 1 }
  }

  const start = Number(startStr)
  if (!Number.isFinite(start) || start < 0 || start >= total) return null
  const end = endStr === '' ? total - 1 : Number(endStr)
  if (!Number.isFinite(end) || end < start) return null
  return { start, end: Math.min(end, total - 1) }
}

function rangedFromFullBlob(blob: Blob, rangeHeader: string): EvidenceRangedFile {
  const total = blob.size
  const parsed = parseSingleByteRange(rangeHeader, total)
  if (!parsed) {
    return {
      blob: new Blob(),
      status: 416,
      contentRange: `bytes */${total}`,
      contentLength: '0',
      acceptRanges: 'bytes',
    }
  }
  const { start, end } = parsed
  return {
    blob: blob.slice(start, end + 1),
    status: 206,
    contentRange: `bytes ${start}-${end}/${total}`,
    contentLength: String(end - start + 1),
    acceptRanges: 'bytes',
  }
}

/** Upstream result for one byte range of an Evidence data parquet object. */
export interface EvidenceRangedFile {
  blob: Blob
  status: number
  contentRange: string | null
  contentLength: string | null
  acceptRanges: string | null
}

export interface UseEvidenceProxy {
  getFile(path: string): Promise<Blob>
  getFile(path: string, options: { range: string }): Promise<Blob | EvidenceRangedFile>
  prefetchFiles(paths: string[]): Promise<void>
  clearCache(): Promise<void>
  getCacheStats(): Promise<CacheStats>
  checkBuildVersion(): Promise<boolean>
  buildVersion: Ref<string | null>
  isLoading: Ref<boolean>
  error: Ref<string | null>
}

/**
 * Evidence Proxy Composable
 */
export function useEvidenceProxy(): UseEvidenceProxy {
  const cache = getEvidenceCache()
  const buildVersion = ref<string | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  /**
   * Get Firebase Auth token
   */
  async function getAuthToken(): Promise<string | null> {
    const user = auth.currentUser
    if (!user) {
      logger.warn('No authenticated user')
      return null
    }

    try {
      return await user.getIdToken()
    } catch (err) {
      logger.error('Failed to get auth token', err)
      return null
    }
  }

  /**
   * Check if an error is a "not found" error (shouldn't retry)
   */
  function isNotFoundError(err: unknown): boolean {
    if (err instanceof Error) {
      const message = err.message.toLowerCase()
      return (
        message.includes('object-not-found') ||
        message.includes('does not exist') ||
        message.includes('404')
      )
    }
    return false
  }

  /**
   * Evidence parquet files under data/. DuckDB probes these with Range.
   */
  function isEvidenceDataParquet(filePath: string): boolean {
    const storagePath = filePath.split('?')[0]
    return storagePath.startsWith('data/') && storagePath.endsWith('.parquet')
  }

  /**
   * Fetch file from Firebase Storage.
   * Range is forwarded only for Evidence data parquet; other files stay full reads.
   */
  async function fetchFromStorage(
    path: string,
    retryCount = 0,
    range?: string,
  ): Promise<Blob | EvidenceRangedFile> {
    const storagePath = `${STORAGE_PREFIX}/${path}`
    const fileRef = storageRef(storage, storagePath)
    const forwardRange =
      typeof range === 'string' && range.length > 0 && isEvidenceDataParquet(path)
    const rangeInit: RequestInit | undefined = forwardRange
      ? { headers: { Range: range } }
      : undefined

    try {
      // Get download URL (requires authentication)
      const downloadUrl = await getDownloadURL(fileRef)

      // Fetch the file. Token URLs are cross-origin and are not intercepted by evidence-sw.js.
      let response: Response
      try {
        response = await fetch(downloadUrl, rangeInit)
      } catch (err) {
        // If normal fetch fails (e.g. due to CORS error from cached opaque response),
        // try again forcing a network reload to get fresh headers
        logger.warn(`Initial fetch failed for ${path}, retrying with cache: 'reload'`, err)
        response = await fetch(downloadUrl, {
          cache: 'reload',
          ...(rangeInit ?? {}),
        })
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const blob = await response.blob()
      if (!forwardRange) {
        return blob
      }

      return {
        blob,
        status: response.status,
        contentRange: response.headers.get('Content-Range'),
        contentLength: response.headers.get('Content-Length'),
        acceptRanges: response.headers.get('Accept-Ranges'),
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'

      // Don't retry for "not found" errors - these won't succeed on retry
      if (isNotFoundError(err)) {
        logger.debug(`File not found: ${path}`)
        throw new Error(`File not found: ${path}`)
      }

      // Handle retries with exponential backoff for transient errors
      if (retryCount < MAX_RETRIES) {
        const delay = INITIAL_RETRY_DELAY * Math.pow(2, retryCount)
        logger.warn(`Retrying ${path} in ${delay}ms (attempt ${retryCount + 1}/${MAX_RETRIES})`)
        await sleep(delay)

        // Check if token needs refresh
        const token = await getAuthToken()
        if (!token) {
          throw new Error('Authentication required')
        }

        return fetchFromStorage(path, retryCount + 1, range)
      }

      logger.error(`Failed to fetch ${path} after ${MAX_RETRIES} retries`, err)
      throw new Error(`Failed to fetch file: ${errorMessage}`)
    }
  }

  /**
   * SHA-256 of `data/manifest.json`. Parquet session keys include this so a new
   * sources build does not reuse the previous parquet bodies.
   */
  async function getManifestHash(): Promise<string> {
    if (!manifestHashPromise) {
      const pending = (async () => {
        const fetched = await fetchFromStorage('data/manifest.json')
        if (!(fetched instanceof Blob)) {
          throw new Error('Refusing to hash a partial data/manifest.json')
        }
        const digest = await crypto.subtle.digest('SHA-256', await fetched.arrayBuffer())
        return hexDigest(digest)
      })()
      manifestHashPromise = pending
      pending.catch(() => {
        if (manifestHashPromise === pending) manifestHashPromise = null
      })
    }
    return manifestHashPromise
  }

  /**
   * Download one data parquet once per manifest version and keep the full body.
   * A Range response is never written into this map.
   */
  async function loadFullParquet(path: string, manifestHash: string): Promise<Blob> {
    const key = parquetSessionKey(path, manifestHash)
    const cached = parquetSessionCache.get(key)
    if (cached) return cached

    const existing = parquetSessionInflight.get(key)
    if (existing) return existing

    const epoch = parquetSessionEpoch
    const pending = (async () => {
      const fetched = await fetchFromStorage(path)
      if (!(fetched instanceof Blob)) {
        throw new Error(`Refusing to cache a partial body for ${path}`)
      }
      if (epoch === parquetSessionEpoch) {
        parquetSessionCache.set(key, fetched)
      }
      return fetched
    })()
    parquetSessionInflight.set(key, pending)
    try {
      return await pending
    } finally {
      if (parquetSessionInflight.get(key) === pending) {
        parquetSessionInflight.delete(key)
      }
    }
  }

  async function getParquetFromSession(
    path: string,
    range?: string,
  ): Promise<Blob | EvidenceRangedFile> {
    const manifestHash = await getManifestHash()
    const full = await loadFullParquet(path, manifestHash)
    if (!range) return full
    return rangedFromFullBlob(full, range)
  }

  /**
   * Check if a file should be cached in IndexedDB.
   * Parquet is session-cached in memory as a full blob (see loadFullParquet).
   * Arrow and api/ responses stay uncached. Never store a parquet slice here.
   */
  function shouldCacheFile(filePath: string): boolean {
    if (filePath.endsWith('.parquet')) return false
    if (filePath.endsWith('.arrow')) return false
    if (filePath.startsWith('data/')) return false
    if (filePath.startsWith('api/')) return false

    // Cache static assets
    return true
  }

  /**
   * Get file with caching. Cache failures are non-fatal — storage fetch still proceeds.
   * `data/*.parquet` is handled above: one full download per manifest version, then
   * Range slices of that blob. This path does not cache arrow or api/ responses.
   */
  async function getFile(path: string): Promise<Blob>
  async function getFile(
    path: string,
    options: { range: string },
  ): Promise<Blob | EvidenceRangedFile>
  async function getFile(
    path: string,
    options?: { range?: string },
  ): Promise<Blob | EvidenceRangedFile> {
    const normalizedPath = path.startsWith('/') ? path.slice(1) : path
    const range = options?.range
    const rangedParquet =
      typeof range === 'string' && range.length > 0 && isEvidenceDataParquet(normalizedPath)

    // DuckDB reaches these objects through the service worker, which calls getFile.
    // Cache the full object here and answer Range from that blob. The worker must
    // not store the 206. Arrow and api/ paths fall through and stay uncached.
    if (isEvidenceDataParquet(normalizedPath)) {
      isLoading.value = true
      error.value = null
      try {
        try {
          return await getParquetFromSession(normalizedPath, rangedParquet ? range : undefined)
        } catch (err) {
          logger.warn('Parquet session cache missed; fetching from storage', {
            path: normalizedPath,
            err,
          })
          return await fetchFromStorage(normalizedPath, 0, rangedParquet ? range : undefined)
        }
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Unknown error'
        throw err
      } finally {
        isLoading.value = false
      }
    }

    const useCache = shouldCacheFile(normalizedPath) && !rangedParquet

    if (!rangedParquet) {
      const existing = inFlightRequests.get(normalizedPath)
      if (existing) {
        logger.debug(`Using in-flight request for ${normalizedPath}`)
        return existing
      }
    }

    const request = (async (): Promise<Blob | EvidenceRangedFile> => {
      try {
        if (useCache) {
          try {
            const cached = await cache.get(normalizedPath)
            if (cached) {
              logger.debug(`Cache hit: ${normalizedPath}`)
              return cached.blob
            }
          } catch (err) {
            logger.warn('Evidence cache read failed; fetching from storage', {
              path: normalizedPath,
              err,
            })
          }
        } else {
          logger.debug(`Skipping cache for data file: ${normalizedPath}`)
        }

        logger.debug(`Fetching: ${normalizedPath}`)
        isLoading.value = true
        error.value = null

        const fetched = await fetchFromStorage(normalizedPath, 0, rangedParquet ? range : undefined)
        const blob = fetched instanceof Blob ? fetched : fetched.blob
        const contentType = getContentType(normalizedPath)

        if (useCache && fetched instanceof Blob) {
          try {
            await cache.set(normalizedPath, blob, contentType)
          } catch (err) {
            logger.warn('Evidence cache write failed; continuing without cache', {
              path: normalizedPath,
              err,
            })
          }
        }

        return fetched
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error'
        error.value = errorMessage
        throw err
      } finally {
        isLoading.value = false
        if (!rangedParquet) {
          inFlightRequests.delete(normalizedPath)
        }
      }
    })()

    if (!rangedParquet) {
      inFlightRequests.set(normalizedPath, request as Promise<Blob>)
    }
    return request
  }

  /**
   * Prefetch multiple files
   */
  async function prefetchFiles(paths: string[]): Promise<void> {
    const promises = paths.map(async (path) => {
      try {
        await getFile(path)
      } catch (err) {
        logger.warn(`Failed to prefetch ${path}`, err)
      }
    })

    await Promise.all(promises)
  }

  /**
   * Clear the cache
   */
  async function clearCache(): Promise<void> {
    clearParquetSessionCache()
    try {
      await cache.clear()
    } catch (err) {
      logger.warn('Failed to clear evidence cache', err)
    }
    buildVersion.value = null
    logger.info('Evidence cache cleared')
  }

  /**
   * Get cache statistics
   */
  async function getCacheStats(): Promise<CacheStats> {
    try {
      return await cache.getStats()
    } catch (err) {
      logger.warn('Failed to read evidence cache stats', err)
      return {
        totalSize: 0,
        fileCount: 0,
        oldestFile: null,
        newestFile: null,
        buildHash: null,
        lastSync: null,
      }
    }
  }

  /**
   * Check build version and invalidate cache if changed
   */
  async function checkBuildVersion(): Promise<boolean> {
    try {
      // Get version from storage metadata
      const versionRef = storageRef(storage, `${STORAGE_PREFIX}/_app/version.json`)

      try {
        const metadata = await getMetadata(versionRef)
        const currentVersion = metadata.md5Hash || metadata.generation?.toString() || null

        if (!currentVersion) {
          logger.warn('No build version found in storage')
          return false
        }

        // Get cached version (best-effort; cache may be unavailable)
        let cachedVersion: string | null = null
        try {
          cachedVersion = await cache.getBuildHash()
        } catch (err) {
          logger.debug('Could not read cached build version', err)
        }

        if (cachedVersion && cachedVersion !== currentVersion) {
          logger.info('Build version changed, clearing cache')
          clearParquetSessionCache()
          try {
            await cache.clear()
            await cache.setBuildHash(currentVersion)
          } catch (err) {
            logger.warn('Failed to update evidence cache after build version change', err)
          }
          buildVersion.value = currentVersion
          return true
        }

        if (!cachedVersion) {
          try {
            await cache.setBuildHash(currentVersion)
          } catch (err) {
            logger.debug('Could not persist build version to cache', err)
          }
        }

        buildVersion.value = currentVersion
        return false // No change
      } catch (err) {
        // Version file might not exist, that's okay
        logger.debug('Could not get build version', err)
        return false
      }
    } catch (err) {
      logger.error('Error checking build version', err)
      return false
    }
  }

  return {
    getFile,
    prefetchFiles,
    clearCache,
    getCacheStats,
    checkBuildVersion,
    buildVersion,
    isLoading,
    error,
  }
}

/**
 * Unregister the Evidence service worker and clear caches
 * Call this on user logout for cleanup
 */
export async function unregisterEvidenceServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    return
  }

  try {
    // Get all service worker registrations
    const registrations = await navigator.serviceWorker.getRegistrations()

    // Find and unregister the Evidence service worker
    for (const registration of registrations) {
      // Check by scope or script URL
      if (
        registration.scope.includes('/admin/') ||
        registration.scope.includes('/evidence-proxy/') ||
        registration.active?.scriptURL.includes('evidence-sw.js')
      ) {
        await registration.unregister()
        logger.info('Evidence service worker unregistered')
      }
    }

    // Clear the Evidence service worker cache
    const cacheNames = await caches.keys()
    for (const cacheName of cacheNames) {
      if (cacheName.startsWith('evidence-sw-cache')) {
        await caches.delete(cacheName)
        logger.info(`Deleted cache: ${cacheName}`)
      }
    }

    // Full parquet bodies are in-memory for the page session, not in the Cache API.
    clearParquetSessionCache()

    // Clear IndexedDB cache only when admin evidence was used this session
    await resetEvidenceCacheForLogout()
  } catch (err) {
    logger.error('Error unregistering Evidence service worker', err)
  }
}
